import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { createPortal } from "react-dom";
import type { TicketBoardCard } from "@kundeninfo/api-client";
import { useTelefon } from "@kundeninfo/ui";
import { Chip, Objektsymbol } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { OwnerBadge } from "./OwnerBadge.js";
import { HIERARCHY_TEXT } from "./hierarchyTexts.js";
import { dueMark, isOpen, outsideFilter } from "./hierarchy.js";

/**
 * DIE RANDLISTE (Spec Teilaufgaben R25, R27; Übergang T-1, Kundeninfo-eigen): die direkten Kinder einer Karte aus der
 * Board-Antwort (kein Nachladen). Sie hängt am `body` und steht `fixed` (Festlegung 6: die Spalten von me-board rollen
 * und schneiden ab).
 *
 * Lage (R25, v006): 4 px rechts neben dem Streifen, so breit wie der Platz bis zum Fensterrand, höchstens 660 px — wo
 * rechts 430 px frei sind, liegt sie damit von selbst bei 430–660 px; in der letzten Spalte wird sie schmaler (bis
 * `RAIL_NARROW`, so schmal wie am kleinsten Telefon), unter 560 px und am Telefon (R27) mit zweizeiligen Zeilen
 * (`tsr--eng`). Nie breiter als das Fenster weniger 12 px; nur wenn auch die schmale Form rechts nicht passt, rückt sie
 * nach links (letzter Ausweg, dann verdeckt sie die Striche). Unten rückt sie ins Fenster. Sie darf Nachbarn verdecken
 * (v006).
 *
 * Schließen: sobald ein Rollfeld rollt, das den Streifen trägt (`scroll` ist nicht `composed`: das Rollfeld der Spalte im
 * Schattenbaum von me-boardspalte meldet sich nur bei sich selbst), bei geänderter Fenstergröße, mit Escape, beim Klick
 * daneben (der Streifen zählt nicht als „daneben") und beim Klick auf eine Zeile. Rollen in der Liste lässt sie offen.
 * Bei Telefon oder grobem Zeiger (`coarse`) trägt ihr Kopf „Im Board zeigen" bzw. „Fokus beenden" (R27, v006).
 */
export const RAIL_MAX = 660;
export const RAIL_GAP = 4;
export const RAIL_NARROW = 240;
export const RAIL_TWO_LINES = 560;
const RAND = 6;

export function SubtaskRail(p: {
  t: TicketBoardCard; anchor: DOMRect; anchorEl: Element; ichId: string; heute: string; shown: ReadonlySet<string>;
  coarse: boolean; focused: boolean; onOeffnen: (id: string) => void; onShowInBoard?: (id: string) => void;
  onEndFocus?: () => void; onPointerEnter: () => void; onPointerLeave: () => void; onClose: () => void;
}): ReactElement {
  const t = p.t;
  const ref = useRef<HTMLDivElement | null>(null);
  const { anchor, anchorEl, onClose } = p;
  useEffect(() => {
    const rollfelder: EventTarget[] = [window];
    for (let n: Node | null = anchorEl; n !== null;) {
      if (n instanceof Element) rollfelder.push(n);
      const hoch: Node | null = (n instanceof Element ? n.assignedSlot : null) ?? n.parentNode;
      n = hoch instanceof ShadowRoot ? hoch.host : hoch;
    }
    const zeiger = (e: PointerEvent) => {
      const amStreifen = e.clientX >= anchor.left && e.clientX <= anchor.right && e.clientY >= anchor.top
        && e.clientY <= anchor.bottom;
      const drin = e.target instanceof Node && ref.current?.contains(e.target) === true;
      if (!drin && !amStreifen) onClose();
    };
    // Escape schließt nur die Liste: vor allen anderen Hörern und verbraucht — ein Board-Fokus endet erst mit dem nächsten
    // (Abschluss-Review M4; der Hörer des Boards prüft `defaultPrevented`).
    const taste = (e: KeyboardEvent) => { if (e.key === "Escape") { e.preventDefault(); onClose(); } };
    for (const r of rollfelder) r.addEventListener("scroll", onClose, { passive: true });
    window.addEventListener("resize", onClose);
    document.addEventListener("pointerdown", zeiger, true);
    document.addEventListener("keydown", taste, true);
    return () => {
      for (const r of rollfelder) r.removeEventListener("scroll", onClose);
      window.removeEventListener("resize", onClose);
      document.removeEventListener("pointerdown", zeiger, true);
      document.removeEventListener("keydown", taste, true);
    };
  }, [anchor, anchorEl, onClose]);
  const fenster = window.innerWidth;
  const von = anchor.right + RAIL_GAP;
  const frei = fenster - von - RAND;
  const breite = Math.min(RAIL_MAX, fenster - 2 * RAND, Math.max(frei, RAIL_NARROW));
  const links = Math.max(RAND, Math.min(von, fenster - breite - RAND));
  const [oben, setOben] = useState(anchor.top);
  useLayoutEffect(() => {
    const hoehe = ref.current?.offsetHeight ?? 0;
    setOben(Math.max(RAND, Math.min(anchor.top, window.innerHeight - hoehe - RAND)));
  }, [anchor.top, t.subtasks.length]);
  const telefon = useTelefon();
  const aussen = outsideFilter(t, p.shown);
  const erledigt = t.subtasks.filter((z) => !isOpen(z.stateKind)).length;
  return createPortal(
    <div ref={ref} className={`tsr${breite < RAIL_TWO_LINES || telefon ? " tsr--eng" : ""}`} role="group"
      aria-label={HIERARCHY_TEXT.railName(t.displayNumber)}
      style={{ position: "fixed", left: `${links}px`, top: `${oben}px`, width: `${breite}px` }}
      onMouseEnter={p.onPointerEnter} onMouseLeave={p.onPointerLeave}>
      <div className="tsr__kopf">
        <Objektsymbol art="teilaufgabe" groesse={16} />
        <span className="tsr__nummer">{t.displayNumber}</span>
        <span className="tsr__titel">{t.title}</span>
        <Chip>{HIERARCHY_TEXT.progress(erledigt, t.subtasks.length)}</Chip>
        {p.coarse && (p.focused
          ? <KiKnopf art="leise" kompakt onClick={() => p.onEndFocus?.()}>{HIERARCHY_TEXT.endFocus}</KiKnopf>
          : p.onShowInBoard !== undefined
            && <KiKnopf art="leise" kompakt onClick={() => p.onShowInBoard!(t.id)}>{HIERARCHY_TEXT.showInBoard}</KiKnopf>)}
      </div>
      <ul className="tsr__liste">
        {t.subtasks.map((z) => {
          const f = dueMark(z.dueDate, p.heute, !isOpen(z.stateKind));
          return (
            <li key={z.id}>
              <button type="button" className="tsr__zeile" onClick={() => { onClose(); p.onOeffnen(z.id); }}>
                <span className="tsr__nummer">{z.displayNumber}</span>
                <span className="tsr__titel">{z.title}</span>
                <span className="tsr__meta">
                  <OwnerBadge owner={z.owner} ichId={p.ichId} mitName />
                  <Chip statusArt={z.status === "ARCHIVED" ? "aus" : isOpen(z.stateKind) ? "laeuft" : "ok"}>
                    {z.status === "ARCHIVED" ? HIERARCHY_TEXT.stateArchived
                      : isOpen(z.stateKind) ? HIERARCHY_TEXT.stateOpen : HIERARCHY_TEXT.stateDone}
                  </Chip>
                  {f !== null && (f.art === null ? <Chip>{f.text}</Chip> : <Chip statusArt={f.art}>{f.text}</Chip>)}
                </span>
              </button>
            </li>
          );
        })}
        {aussen > 0 && <li className="tsr__aussen"><Chip statusArt="warnung">{HIERARCHY_TEXT.outsideFilter(aussen)}</Chip></li>}
      </ul>
    </div>,
    document.body);
}
